import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, MailPlusIcon, DownloadIcon, ArrowRightIcon, CheckIcon, XIcon, MapPinIcon, StarIcon, PhoneIcon, ClockIcon } from 'lucide-react';
const tabs = [{
  id: 'scrape',
  icon: SearchIcon,
  title: 'Scrape Google Maps at scale',
  description: 'Stop copying data one business at a time. Our google map scraper extracts thousands of google local business listings with ratings, reviews, addresses, and phone numbers in minutes — not days. Works as a google maps phone number scraper and google reviews scraper too.'
}, {
  id: 'enrich',
  icon: MailPlusIcon,
  title: 'Enrich with emails & owner contacts',
  description: "Google Maps doesn't show emails. Our Google Maps email extractor finds verified owner emails, direct phone numbers, and decision-maker names that aren't publicly listed."
}, {
  id: 'export',
  icon: DownloadIcon,
  title: 'Export clean, ready-to-use data',
  description: 'Download enriched local business data as CSV, or push directly to Salesforce, HubSpot, and other CRMs. No cleanup needed — every record is structured, deduplicated, and verified.'
}];
export function ScraperWorkflow() {
  const [activeTab, setActiveTab] = useState(tabs[0].id);
  return <section className="py-24 bg-white" data-id="element-2372">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2373">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2374">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-2375">
            Turn <span className="text-blue-600" data-id="element-2376">Google Maps</span> into your
            lead engine
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-2377">
            What takes hours of manual work, Openmart does in minutes. Extract,
            enrich, and export local business data at scale.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-center" data-id="element-2378">
          {/* Left: Tabs */}
          <div className="lg:col-span-5 space-y-2" data-id="element-2379">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full text-left p-8 rounded-[2rem] transition-all duration-300 ${isActive ? 'bg-slate-50' : 'bg-white hover:bg-slate-50/50'}`} data-id="element-2380">
                  <div className="flex items-start gap-6" data-id="element-2381">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-2382">
                      <tab.icon className="w-6 h-6" data-id="element-2383" />
                    </div>
                    <div data-id="element-2384">
                      <h3 className={`text-xl font-bold mb-3 transition-colors ${isActive ? 'text-slate-900' : 'text-slate-700'}`} data-id="element-2385">
                        {tab.title}
                      </h3>
                      <p className={`text-base leading-relaxed transition-colors ${isActive ? 'text-slate-600' : 'text-slate-500'}`} data-id="element-2386">
                        {tab.description}
                      </p>
                    </div>
                  </div>
                </button>;
          })}
          </div>

          {/* Right: Dynamic Visuals */}
          <div className="lg:col-span-7" data-id="element-2387">
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-10 min-h-[450px] flex items-center justify-center relative overflow-hidden" data-id="element-2388">
              <AnimatePresence mode="wait" data-id="element-2389">
                {activeTab === 'scrape' && <motion.div key="scrape" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full" data-id="element-2390">
                    <div className="flex items-center justify-between mb-6 px-2" data-id="element-2391">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-2" data-id="element-2392">
                        <ClockIcon className="w-3.5 h-3.5" data-id="element-2393" />
                        Manual: 40+ hours
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-2394" />
                      <div className="text-xs font-bold text-blue-600 uppercase tracking-wider flex items-center gap-2" data-id="element-2395">
                        <CheckIcon className="w-3.5 h-3.5" data-id="element-2396" />
                        Openmart: 2 minutes
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-2397">
                      {/* Before card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm space-y-3" data-id="element-2398">
                        <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-2399">
                          Before
                        </div>
                        <div className="space-y-2.5" data-id="element-2400">
                          <div className="flex items-center gap-2 text-xs text-slate-400" data-id="element-2401">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-2402" />
                            <span data-id="element-2403">Copy business name...</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-400" data-id="element-2404">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-2405" />
                            <span data-id="element-2406">Copy address...</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-400" data-id="element-2407">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-2408" />
                            <span data-id="element-2409">Copy phone number...</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-2410">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-2411" />
                            <span data-id="element-2412">No email available</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-2413">
                            <div className="w-4 h-4 rounded bg-slate-100" data-id="element-2414" />
                            <span data-id="element-2415">No owner info</span>
                          </div>
                        </div>
                        <div className="pt-3 border-t border-slate-100 text-center" data-id="element-2416">
                          <span className="text-xs text-red-400 font-semibold" data-id="element-2417">
                            ~3 min per business
                          </span>
                        </div>
                      </div>

                      {/* After card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm border border-blue-100 space-y-3" data-id="element-2418">
                        <div className="flex items-center justify-between mb-1" data-id="element-2419">
                          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-2420">
                            After
                          </div>
                          <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full" data-id="element-2421">
                            1,245 results
                          </span>
                        </div>
                        <div className="space-y-2" data-id="element-2422">
                          {['Brooklyn Roasting Co.', 'Devocion Coffee', 'Sey Coffee'].map((name, i) => <div key={i} className="flex items-center gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2" data-id="element-2423">
                              <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-[10px] font-bold flex-shrink-0" data-id="element-2424">
                                {name[0]}
                              </div>
                              <div className="min-w-0" data-id="element-2425">
                                <div className="font-semibold text-slate-900 truncate" data-id="element-2426">
                                  {name}
                                </div>
                                <div className="text-slate-400 flex items-center gap-1.5" data-id="element-2427">
                                  <StarIcon className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" data-id="element-2428" />
                                  4.{7 + i}
                                  <span className="mx-0.5" data-id="element-2429">·</span>
                                  <MapPinIcon className="w-2.5 h-2.5" data-id="element-2430" />
                                  Brooklyn
                                </div>
                              </div>
                            </div>)}
                        </div>
                        <div className="pt-3 border-t border-slate-100 text-center" data-id="element-2431">
                          <span className="text-xs text-green-600 font-semibold" data-id="element-2432">
                            All 1,245 in 2 minutes
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'enrich' && <motion.div key="enrich" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-xl" data-id="element-2433">
                    <div className="flex items-center justify-between mb-6 px-2" data-id="element-2434">
                      <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-2435">
                        Google Maps data
                      </div>
                      <ArrowRightIcon className="w-5 h-5 text-slate-300 flex-shrink-0" data-id="element-2436" />
                      <div className="text-xs font-bold text-blue-600 uppercase tracking-wider" data-id="element-2437">
                        + Openmart enrichment
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-2438">
                      {/* Before card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm space-y-4" data-id="element-2439">
                        <div className="flex items-center gap-3 mb-2" data-id="element-2440">
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 text-sm font-bold" data-id="element-2441">
                            BR
                          </div>
                          <div data-id="element-2442">
                            <div className="text-sm font-bold text-slate-900" data-id="element-2443">
                              Brooklyn Roasting
                            </div>
                            <div className="text-xs text-slate-400" data-id="element-2444">
                              25 Jay St, Brooklyn
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2.5 pt-2 border-t border-slate-100" data-id="element-2445">
                          <div className="flex items-center gap-2 text-xs" data-id="element-2446">
                            <CheckIcon className="w-4 h-4 text-slate-300" data-id="element-2447" />
                            <span className="text-slate-500" data-id="element-2448">
                              (718) 855-1000
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-2449">
                            <XIcon className="w-4 h-4" data-id="element-2450" />
                            <span data-id="element-2451">No email available</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-2452">
                            <XIcon className="w-4 h-4" data-id="element-2453" />
                            <span data-id="element-2454">No owner info</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-300" data-id="element-2455">
                            <XIcon className="w-4 h-4" data-id="element-2456" />
                            <span data-id="element-2457">No social profiles</span>
                          </div>
                        </div>
                      </div>

                      {/* After card */}
                      <div className="bg-white p-5 rounded-2xl shadow-sm border border-blue-100 space-y-4" data-id="element-2458">
                        <div className="flex items-center gap-3 mb-2" data-id="element-2459">
                          <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-bold" data-id="element-2460">
                            BR
                          </div>
                          <div data-id="element-2461">
                            <div className="text-sm font-bold text-slate-900" data-id="element-2462">
                              Brooklyn Roasting
                            </div>
                            <div className="text-xs text-slate-500" data-id="element-2463">
                              25 Jay St, Brooklyn
                            </div>
                          </div>
                        </div>
                        <div className="space-y-2.5 pt-2 border-t border-slate-100" data-id="element-2464">
                          <div className="flex items-center gap-2 text-xs" data-id="element-2465">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-2466" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-2467">
                              (718) 855-1000
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-id="element-2468">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-2469" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-2470">
                              info@brooklynroasting.com
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-id="element-2471">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-2472" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-2473">
                              Jim Munson, Founder
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-xs" data-id="element-2474">
                            <CheckIcon className="w-4 h-4 text-green-500" data-id="element-2475" />
                            <span className="text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded" data-id="element-2476">
                              @brooklynroasting
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}

                {activeTab === 'export' && <motion.div key="export" initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.2
              }} className="w-full max-w-md" data-id="element-2477">
                    <div className="bg-white rounded-3xl p-8 shadow-sm border border-slate-100 text-center" data-id="element-2478">
                      <div className="flex justify-center items-center gap-3 mb-8" data-id="element-2479">
                        <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm" data-id="element-2480">
                          <MapPinIcon className="w-7 h-7 text-blue-600" data-id="element-2481" />
                        </div>
                        <div className="flex flex-col items-center" data-id="element-2482">
                          <ArrowRightIcon className="w-5 h-5 text-blue-400 mb-1" data-id="element-2483" />
                          <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider" data-id="element-2484">
                            Export
                          </span>
                        </div>
                        <div className="flex gap-2" data-id="element-2485">
                          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 shadow-sm" data-id="element-2486">
                            <span className="font-bold text-sm text-blue-600" data-id="element-2487">
                              CSV
                            </span>
                          </div>
                          <div className="w-14 h-14 bg-blue-50 rounded-2xl flex items-center justify-center border border-blue-100 shadow-sm" data-id="element-2488">
                            <span className="font-bold text-[10px] text-blue-600 leading-tight text-center" data-id="element-2489">
                              Sales
                              <br data-id="element-2490" />
                              force
                            </span>
                          </div>
                          <div className="w-14 h-14 bg-orange-50 rounded-2xl flex items-center justify-center border border-orange-100 shadow-sm" data-id="element-2491">
                            <span className="font-bold text-[10px] text-orange-600 leading-tight text-center" data-id="element-2492">
                              Hub
                              <br data-id="element-2493" />
                              Spot
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-slate-50 rounded-xl p-5 text-left border border-slate-100" data-id="element-2494">
                        <div className="flex items-center gap-3 mb-4" data-id="element-2495">
                          <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center" data-id="element-2496">
                            <CheckIcon className="w-5 h-5 text-green-600" data-id="element-2497" />
                          </div>
                          <div data-id="element-2498">
                            <h4 className="font-bold text-slate-900 text-sm" data-id="element-2499">
                              Export ready
                            </h4>
                            <p className="text-xs text-slate-500" data-id="element-2500">
                              brooklyn_coffee_shops.csv
                            </p>
                          </div>
                        </div>
                        <div className="space-y-3" data-id="element-2501">
                          <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-2502">
                            <div className="bg-white rounded-lg p-3 border border-slate-100" data-id="element-2503">
                              <div className="text-2xl font-bold text-slate-900" data-id="element-2504">
                                1,245
                              </div>
                              <div className="text-slate-500" data-id="element-2505">businesses</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-slate-100" data-id="element-2506">
                              <div className="text-2xl font-bold text-slate-900" data-id="element-2507">
                                89%
                              </div>
                              <div className="text-slate-500" data-id="element-2508">have emails</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-xs" data-id="element-2509">
                            <div className="bg-white rounded-lg p-3 border border-slate-100" data-id="element-2510">
                              <div className="text-2xl font-bold text-slate-900" data-id="element-2511">
                                94%
                              </div>
                              <div className="text-slate-500" data-id="element-2512">have phones</div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-slate-100" data-id="element-2513">
                              <div className="text-2xl font-bold text-slate-900" data-id="element-2514">
                                50+
                              </div>
                              <div className="text-slate-500" data-id="element-2515">data fields</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}